"use strict";

import ahk from "../antihackkey.json";
import version from "../version.json";

type CommandArgType = "string" | "number" | "boolean";
type CommandArg = {
  name: string;
  type: CommandArgType;
  optional: boolean;
};
type CommandRun = (args: string[], rest: string) => void;
type LocalCommand = {
  kind: "local";
  args: CommandArg[];
  desc: string;
  run: CommandRun;
};
type ServerCommand = {
  kind: "server";
  args: CommandArg[];
  desc: string;
};
type Command = LocalCommand | ServerCommand;
type GameLike = Krunker.Game & Krunker.AnyRecord;
type ModeEntry = {
  index: number;
  mode: Krunker.AnyRecord;
  alias: string;
  name: string;
};
type ResolveReason =
  | "ambiguous"
  | "empty"
  | "no-maps"
  | "no-modes"
  | "not-found"
  | null;
type IndexResolve<TExtra extends Krunker.AnyRecord = Krunker.AnyRecord> =
  TExtra & {
    index: number | null;
    reason: ResolveReason;
  };
type AIEntity = Krunker.AnyRecord & {
  active?: boolean;
  kill?: (source?: Krunker.Player | null) => void;
  name?: string;
  sid?: number | string;
};
type OfflineKillResolve =
  | { kind: "player"; target: Krunker.Player; reason: null }
  | { kind: "ai"; target: AIEntity; reason: null }
  | { kind: null; target: null; reason: "ambiguous" | "empty" | "not-found" };
type ForceCollideObject = Krunker.AnyRecord & {
  collidable?: boolean;
  meshRef?: Krunker.AnyRecord & { collidable?: boolean };
  noCol?: boolean;
};
type ForceCollideInjectedObject = ForceCollideObject & {
  __forceColInjected: true;
  __forceColSourceIndex: number;
};
type ForceCollideSourceObject = Krunker.AnyRecord & {
  col?: unknown;
  i?: unknown;
  id?: unknown;
  l?: unknown;
  p?: unknown;
  s?: unknown;
};
type ForceCollideEntry = {
  obj: ForceCollideObject;
  hasNoCol: boolean;
  noCol: boolean | undefined;
  hasCollidable: boolean;
  collidable: boolean | undefined;
  hasMeshCollidable: boolean;
  meshCollidable: boolean | undefined;
};
type ForceCollideState = {
  enabled: boolean;
  entries: ForceCollideEntry[];
  injected: ForceCollideInjectedObject[];
};
type ConsoleGeom = {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
};
type WrappedHandler = ((...args: any[]) => any) & {
  __devConsoleWrapped?: boolean;
};

// Source-engine style developer console for Krunker.
// ─────────────────────────────────────────────────────────
// • `~` (handled in cheats.js) toggles visibility.
// • Type a command, press Enter. Up/Down navigates history.
// • Tab completes the current token.
// • An argument hint row renders under the input as you type
//   (mimicking Source's auto-suggest with `number?` `boolean?`).
// • Output area mirrors console.log/warn/error and incoming
//   chat (`ch` packets). Submitted commands echo as `] <cmd>`.
//
// Commands either run locally (registered via `registerLocal`)
// or are forwarded to the server as `/cmd args` over the chat
// packet (`io.send("ct", "/cmd args")`). The server already
// recognises a large set of slash commands in
// server/packetHandlers.ts — those are mirrored here so the
// console can advertise them in autocomplete/help.
// ─────────────────────────────────────────────────────────

// ─── DOM scaffolding ────────────────────────────────────

// Inject a stylesheet so we can use scoped selectors (scrollbar,
// pseudo-elements, hover states) — inline styles can't do those.
const style = document.createElement("style");
style.textContent = `
@font-face {
  font-family: "LuconDevConsole";
  src: url("/css/fonts/lucon.ttf") format("truetype");
  font-display: block;
}
#devConsoleOverlay {
  position: fixed;
  inset: 0;
  display: none;
  z-index: 999999;
  background: transparent;
  pointer-events: none;
}
#devConsoleOverlay * {
  font-family: "LuconDevConsole", "Lucida Console", "Consolas", "Liberation Mono", "Courier New", monospace;
  -webkit-font-smoothing: none;
  -moz-osx-font-smoothing: grayscale;
  font-smooth: never;
  text-rendering: geometricPrecision;
}
#devConsoleOverlay .modal {
  opacity: 0.95;
  position: absolute;
  right: 8px;
  top: 8px;
  padding: 7px;
  border-radius: 5px;
  width: min(744px, calc(100vw - 16px));
  height: min(280px, calc(100vh - 16px));
  background: #7a7a7a;
  display: flex;
  flex-direction: column;
  color: #d0d0d0;
  font-size: 12px;
  pointer-events: auto;
  min-width: 280px;
  min-height: 160px;
}
#devConsoleOverlay .titlebar {
  background: #7a7a7a;
  font-family: inherit;
  font-size: 10px;
  font-weight: normal;
  padding: 3px 6px;
  user-select: none;
  cursor: move;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 18px;
  flex: 0 0 auto;
}
#devConsoleOverlay .titlebar span {
  color: #fff;
  font-weight: bold;
}
#devConsoleOverlay .titlebar .x {
  width: 18px;
  height: 16px;
  background: transparent;
  border: 0;
  color: #d0d0d0;
  font-family: inherit;
  font-size: 10px;
  line-height: 14px;
  text-align: center;
  cursor: pointer;
  user-select: none;
}
#devConsoleOverlay .titlebar .x:hover { color: #fff; }
#devConsoleOverlay .body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 6px 6px 5px;
  gap: 2px;
  overflow: hidden;
  background: #7a7a7a;
}
#devConsoleOverlay .logs {
  flex: 1 1 auto;
  overflow-y: scroll;
  background: #6a6a6a;
  border: 1px #282828 inset;
  padding: 3px 5px;
  white-space: pre-wrap;
  word-break: break-word;
  color: #e0e0e0;
  line-height: 1.25;
  font-family: inherit;
  font-size: 12px;
}

#devConsoleOverlay .logs, #devConsoleOverlay .logs * {
  user-select: text !important;
  -webkit-user-select: text !important;
  cursor: text;
}

#devConsoleOverlay .logs ::selection,
#devConsoleOverlay .logs::selection {
  background: #f39c00;
  color: #000000;
}
#devConsoleOverlay .logs::-webkit-scrollbar { width: 14px; }
#devConsoleOverlay .logs::-webkit-scrollbar-track {
  background: #4a4a4a;
}
#devConsoleOverlay .logs::-webkit-scrollbar-thumb {
  background: #7a7a7a;
  border: 1px solid #3a3a3a;
}
#devConsoleOverlay .hints {
  position: absolute;
  left: 0;
  right: 70px;
  bottom: calc(100% + 1px);
  display: none;
  max-height: 180px;
  overflow-y: auto;
  background: #6f6f6f;
  border: 1px solid #4c4c4c;
  color: #d8d8d8;
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
  z-index: 2;
}
#devConsoleOverlay .hints.isOpen { display: block; }
#devConsoleOverlay .hintItem {
  padding: 2px 6px;
  cursor: default;
}
#devConsoleOverlay .hintItem.isActive {
  background: #5f5f5f;
  color: #ffffff;
}
#devConsoleOverlay .hintMeta {
  color: #b8b8b8;
}
#devConsoleOverlay .inputShell {
  position: relative;
  flex: 0 0 auto;
  margin-top: 1px;
}
#devConsoleOverlay .row {
  display: flex;
  gap: 6px;
  align-items: stretch;
}
#devConsoleOverlay .row input {
  flex: 1 1 auto;
  background: #585858;
  outline: 0;
  color: #e0e0e0;
  font-family: inherit;
  font-size: 12px;
  padding: 3px 5px;
  min-width: 0;
  border: 1px #282828 inset;
}
#devConsoleOverlay .row button[type="submit"] {
  background-color: #7a7a7a;
  width: 64px;
  border: 1px #a2a2a2 outset;
  font-size: 10px;
}

#devConsoleOverlay .row button {
  background: #7a7a7a;
  border: 1px solid #3a3a3a;
  font-family: inherit;
  font-size: 12px;
  padding: 3px 0;
  cursor: pointer;
  color: #e0e0e0;
}
#devConsoleOverlay .row button:hover { background: #8a8a8a; }
#devConsoleOverlay .row button:active { background: #6a6a6a; }
#devConsoleOverlay .grip {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
  background:
    linear-gradient(135deg, transparent 0 6px, #9a9a9a 6px 7px, transparent 7px 9px, #9a9a9a 9px 10px, transparent 10px 12px, #9a9a9a 12px 13px, transparent 13px);
}
`;
(document.head || document.documentElement).appendChild(style);

const overlay = document.createElement("div");
overlay.id = "devConsoleOverlay";

const modal = document.createElement("div");
modal.className = "modal";

const header = document.createElement("div");
header.className = "titlebar";
const headerLabel = document.createElement("span");
headerLabel.textContent = "Console";
const closeBtn = document.createElement("span");
closeBtn.className = "x";
closeBtn.textContent = "✕";
closeBtn.addEventListener("click", () => toggleConsole(false));
header.append(headerLabel, closeBtn);

const body = document.createElement("div");
body.className = "body";

const logs = document.createElement("div");
logs.className = "logs";
// Make logs focusable so Ctrl+A inside it can be scoped to just the logs.
logs.tabIndex = -1;
logs.style.outline = "none";
logs.addEventListener("mousedown", () => {
  // Focus on click so subsequent keydown is targeted at logs.
  setTimeout(() => logs.focus({ preventScroll: true }), 0);
});
logs.addEventListener("keydown", (e) => {
  if ((e.ctrlKey || e.metaKey) && (e.key === "a" || e.key === "A")) {
    e.preventDefault();
    e.stopPropagation();
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(logs);
    sel.removeAllRanges();
    sel.addRange(range);
  }
});

const hints = document.createElement("div");
hints.className = "hints";

const inputShell = document.createElement("div");
inputShell.className = "inputShell";

// Use a <form> so pressing Enter inside the input dispatches a
// `submit` event — this fires even if Krunker's engine installed a
// capture-phase keydown listener that calls stopImmediatePropagation
// on Enter (which would otherwise prevent our own keydown handler
// from ever seeing it).
const inputRow = document.createElement("form");
inputRow.className = "row";

const input = document.createElement("input");
input.type = "text";
input.autocomplete = "off";
input.spellcheck = false;

const submitBtn = document.createElement("button");
submitBtn.type = "submit";
submitBtn.textContent = "Submit";

const grip = document.createElement("div");
grip.className = "grip";

inputRow.append(input, submitBtn);
inputShell.append(hints, inputRow);
body.append(logs, inputShell);
modal.append(header, body, grip);
overlay.append(modal);

// Defer attaching until <body> exists (this file is required during
// hook bootstrap, possibly before DOMContentLoaded).
const attach = (): HTMLElement | false =>
  document.body && document.body.appendChild(overlay);
if (document.body) attach();
else document.addEventListener("DOMContentLoaded", attach, { once: true });

// ─── Output sink ────────────────────────────────────────

// Source/HL2 console palette: white background, black body text,
// red for warnings/errors, dim gray for hints, dark for echoed cmds.
const COLORS = {
  log: "#e0e0e0",
  info: "#9ac8ff",
  warn: "#ffb84d",
  error: "#ff6a6a",
  cmd: "#ffffff",
  reply: "#9affb0",
  hint: "#b0b0b0",
};
type ConsoleKind = keyof typeof COLORS;

function append(text: unknown, kind: ConsoleKind = "log"): void {
  const line = document.createElement("div");
  line.style.color = COLORS[kind] || COLORS.log;
  line.textContent = String(text);
  // Auto-scroll only if the user is already at the bottom — mirrors
  // Source's behavior so reading scrollback isn't disrupted.
  const atBottom = logs.scrollTop + logs.clientHeight >= logs.scrollHeight - 4;
  logs.appendChild(line);
  while (logs.childElementCount > 1000) logs.removeChild(logs.firstChild);
  if (atBottom) logs.scrollTop = logs.scrollHeight;
}

export const print = (text: unknown): void => append(text, "log");

// Mirror console.* — keep originals intact for devtools.
for (const k of ["log", "info", "warn", "error"] as const) {
  const orig = console[k].bind(console) as (...args: unknown[]) => void;
  console[k] = function (...args: unknown[]): void {
    try {
      const text = args
        .map((a) => {
          if (typeof a === "string") return a;
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        })
        .join(" ");
      append(text, k);
    } catch {
      /* never let logging crash the game */
    }
    orig(...args);
  } as Console[typeof k];
}

// ─── Command registry ───────────────────────────────────

const cmds: Record<string, Command> = Object.create(null);

function arg(
  name: string,
  type: CommandArgType = "string",
  optional = false,
): CommandArg {
  return { name, type, optional };
}

function registerLocal(
  name: string,
  args: CommandArg[],
  desc: string,
  run: CommandRun,
): void {
  cmds[name] = { kind: "local", args, desc, run };
}

function registerServer(name: string, args: CommandArg[], desc: string): void {
  cmds[name] = { kind: "server", args, desc };
}

// ── Local commands ─────────────────────────────────────

registerLocal("help", [arg("cmd", "string", true)], "list commands", (a) => {
  if (a[0]) {
    const c = cmds[a[0]];
    if (!c) return append(`unknown command: ${a[0]}`, "error");
    append(`${a[0]} ${fmtArgs(c.args)} — ${c.desc}`, "info");
    return;
  }
  const names = Object.keys(cmds).sort();
  append(`${names.length} commands:`, "info");
  for (const n of names) append(`  ${n.padEnd(18)} ${cmds[n].desc}`, "hint");
});

registerLocal("clear", [], "clear console output", () => {
  logs.innerHTML = "";
});

registerLocal("echo", [arg("text", "string")], "print text to console", (a) =>
  append(a.join(" ")),
);

registerLocal("print", [arg("text", "string")], "alias for echo", (a) =>
  append(a.join(" ")),
);

registerLocal(
  "bind",
  [arg("key", "string", true), arg("command", "string", true)],
  "bind a key to a command (session only)",
  (a) => {
    if (!a[0]) {
      const keys = Object.keys(keyBinds);
      if (!keys.length) return append("no binds", "hint");
      for (const k of keys) append(`  ${k} = ${keyBinds[k]}`, "hint");
      return;
    }
    if (!a[1]) {
      append(`${a[0]} = ${keyBinds[a[0]] || "(unbound)"}`, "info");
      return;
    }
    keyBinds[a[0]] = a.slice(1).join(" ");
    append(`bound ${a[0]} -> ${keyBinds[a[0]]}`, "info");
  },
);

registerLocal("unbind", [arg("key", "string")], "remove a key bind", (a) => {
  if (!a[0]) return;
  delete keyBinds[a[0]];
  append(`unbound ${a[0]}`, "info");
});

registerLocal(
  "alias",
  [arg("name", "string", true), arg("command", "string", true)],
  "define a command alias",
  (a) => {
    if (!a[0]) {
      const keys = Object.keys(aliases);
      if (!keys.length) return append("no aliases", "hint");
      for (const n of keys) append(`  ${n} = ${aliases[n]}`, "hint");
      return;
    }
    if (!a[1]) {
      append(`${a[0]} = ${aliases[a[0]] || "(undefined)"}`, "info");
      return;
    }
    const body = a.slice(1).join(" ");
    aliases[a[0]] = body;
    registerLocal(a[0], [], `alias for: ${body}`, () => exec(body));
    append(`alias ${a[0]} -> ${body}`, "info");
  },
);

registerLocal(
  "exec",
  [arg("commands", "string")],
  "run multiple commands separated by ;",
  (a) => {
    for (const part of a.join(" ").split(";")) {
      const t = part.trim();
      if (t) exec(t);
    }
  },
);

type ConnectTarget = {
  label: string;
  matchmakerURL: string;
  socialURL: string;
  apiURL: string;
  enableHttps: boolean;
  allowHosts: string[];
};

function trimEndpoint(url: string): string {
  return url.replace(/\/+$/, "");
}

function isLocalConnectHost(hostname: string): boolean {
  return (
    hostname === "127.0.0.1" ||
    hostname === "localhost" ||
    hostname === "0.0.0.0" ||
    hostname.startsWith("192.168.")
  );
}

function endpointHost(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

function withUniqueHosts(hosts: Array<string | null>): string[] {
  const out: Record<string, boolean> = {};
  for (const host of hosts) {
    if (!host) continue;
    out[host.toLowerCase()] = true;
  }
  return Object.keys(out);
}

function resolveConnectTarget(input: string): ConnectTarget | null {
  const raw = input.trim();
  const target = raw || "local";
  const local =
    target.toLowerCase() === "local" || target.toLowerCase() === "default";
  if (local) {
    return {
      label: "local",
      matchmakerURL: location.origin + "/mm",
      socialURL: location.origin + "/api",
      apiURL: location.origin + "/api",
      enableHttps: location.protocol === "https:",
      allowHosts: [],
    };
  }

  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(target);
  let url: URL;
  try {
    url = new URL(hasScheme ? target : "https://" + target);
  } catch {
    return null;
  }

  const hostname = url.hostname.toLowerCase();
  if (!hostname) return null;

  if (isLocalConnectHost(hostname) || url.port) {
    const origin = trimEndpoint(url.origin);
    return {
      label: url.host,
      matchmakerURL: origin + "/mm",
      socialURL: origin + "/api",
      apiURL: origin + "/api",
      enableHttps: url.protocol === "https:",
      allowHosts: [hostname],
    };
  }

  const root = hostname.replace(/^(matchmaker|social|api)\./, "");
  const matchmakerURL = "https://matchmaker." + root;
  const apiURL = "https://api." + root;
  const socialURL = root === "krunker.zip" ? apiURL : "https://social." + root;

  return {
    label: root,
    matchmakerURL,
    socialURL,
    apiURL,
    enableHttps: true,
    allowHosts: withUniqueHosts([
      root,
      endpointHost(matchmakerURL),
      endpointHost(socialURL),
      endpointHost(apiURL),
    ]),
  };
}

function applyConnectTarget(target: ConnectTarget): void {
  const state =
    window.__krunkerConnectState ||
    (window.__krunkerConnectState = { allowRemoteWsHosts: {} });
  state.matchmakerURL = target.matchmakerURL;
  state.socialURL = target.socialURL;
  state.apiURL = target.apiURL;
  state.allowRemoteWsHosts = {};
  for (const host of target.allowHosts) state.allowRemoteWsHosts[host] = true;

  if (window.config) {
    window.config.matchmakerURL = target.matchmakerURL;
    window.config.socialURL = target.socialURL;
    window.config.apiURL = target.apiURL;
    window.config.enableHttps = target.enableHttps;
  }
  if (window.client) {
    window.client.matchmakerAddress = target.matchmakerURL;
  }
}

function clearPingRegionCache(): void {
  try {
    const cacheKey =
      typeof ClientManager?.PING_REGION_CACHE_KEY === "string"
        ? ClientManager.PING_REGION_CACHE_KEY
        : "pingRegion7";
    localStorage.removeItem(cacheKey);
  } catch {}
}

function formatConnectError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === "object") {
    const rec = err as Error & { response: { data: { error: Error } } };
    if (rec.message) return String(rec.message);
    if (rec.response?.data?.error) return String(rec.response.data.error);
  }
  return String(err || "unknown error");
}

function logConnectError(err: unknown): void {
  append("connect failed: " + formatConnectError(err), "error");
  console.error("[devConsole] connect failed", err);
}

async function seekThroughConnectTarget(): Promise<boolean> {
  if (
    !window.client ||
    typeof window.client.seek !== "function" ||
    typeof window.joinServer !== "function"
  ) {
    return false;
  }

  try {
    window.history.replaceState(document.title, document.title, "/");
  } catch {}

  const mmToken =
    typeof window.__LOADER__mmTokenPromise === "undefined"
      ? null
      : await window.__LOADER__mmTokenPromise;
  const seekResult = await window.client.seek({
    autoChangeGame: false,
    dataQuery: {
      v: window.config?.useLooseClient ? undefined : version,
    },
    validationToken: mmToken || undefined,
  });

  window.joinServer(
    seekResult.host,
    seekResult.port,
    seekResult.gameId,
    seekResult.clientId,
    null,
  );
  return true;
}

registerLocal(
  "connect",
  [arg("hostname", "string", true)],
  "seek and connect through a matchmaker hostname",
  async (a, rest) => {
    const input = (rest || a.join(" ")).trim();
    const target = resolveConnectTarget(input);
    if (!target) {
      append("connect <local|hostname>", "error");
      return;
    }

    applyConnectTarget(target);
    clearPingRegionCache();

    append(`Connecting through ${target.label}...`, "info");
    append(`  Matchmaker: ${target.matchmakerURL}`, "info");
    append(`  Social: ${target.socialURL}`, "info");
    append(`  API: ${target.apiURL}`, "info");

    try {
      const seeking = await seekThroughConnectTarget();
      if (seeking) return;
    } catch (err) {
      logConnectError(err);
      return;
    }

    if (window.io && typeof window.io.close === "function") {
      window.io.close();
    }
    append("endpoint updated; live seek is unavailable on this page", "warn");
  },
);

registerLocal(
  "disconnect",
  [],
  "disconnect and reset to default endpoints",
  () => {
    const state = window.__krunkerConnectState;
    if (state) {
      state.matchmakerURL = undefined;
      state.socialURL = undefined;
      state.apiURL = undefined;
      state.allowRemoteWsHosts = {};
    }
    clearPingRegionCache();
    if (window.io && typeof window.io.close === "function") {
      window.io.close();
    }
    location.href = "/";
  },
);

registerLocal("quit", [], "close the window", () => {
  window.close();
});

registerLocal("reconnect", [], "reload the current page", () => {
  location.reload();
});

declare function playCustomMap(): void;

registerLocal("offline", [], "switch to offline mode", () => {
  try {
    playCustomMap();
    clearPingCounter();
  } catch (err) {
    clearPingCounter();
    append("offline failed: " + (err && err.message), "error");
  }
});

registerLocal("ahk", [], "show current antihack key", () => {
  append(`IO AHK: ${io.ahNum} (base: ${ahk})`, "info");
});

registerLocal("version", [], "show current client version", () => {
  append(`Version: ${version}`, "info");
});

registerLocal("build", [], "alias for version", () => {
  append(`Version: ${version}`, "info");
});

registerLocal("verison", [], "alias for version", () => {
  append(`Version: ${version}`, "info");
});

registerLocal(
  "toggle",
  [arg("var", "string")],
  "toggle a server boolean command (god, noclip, ...)",
  (a) => {
    const v = a[0];
    if (!v) return append("toggle <var>", "error");
    sendServer(`/${v}`);
  },
);

registerLocal("say", [arg("text", "string")], "say something in chat", (a) => {
  const text = a.join(" ").trim();
  if (!text) return;
  sendServer(text);
});

registerLocal("status", [], "show current server connection details", () => {
  printServerDetails();
});

registerLocal("serverinfo", [], "alias for status", () => {
  printServerDetails();
});

function getLocalTransformPlayer(): Krunker.Player | null {
  if (!player) {
    append("no local player", "error");
    return null;
  }
  return player;
}

function formatTransformValue(value: unknown): string {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(6) : "0.000000";
}

function printLocalPosition(): void {
  if (!player) return;
  append(
    `position: ${formatTransformValue(player.x)} ${formatTransformValue(player.y)} ${formatTransformValue(player.z)}`,
    "info",
  );
}

function printLocalRotation(): void {
  const player = getLocalTransformPlayer();
  if (!player) return;
  append(
    `rotation: yaw=${formatTransformValue(player.yaw)} pitch=${formatTransformValue(player.pitch)} xDire=${formatTransformValue(player.xDire)}`,
    "info",
  );
}

registerLocal("pos", [], "show local player position", () => {
  printLocalPosition();
});

registerLocal("position", [], "alias for pos", () => {
  printLocalPosition();
});

registerLocal("rot", [], "show local player rotation", () => {
  printLocalRotation();
});

registerLocal("rotation", [], "alias for rot", () => {
  printLocalRotation();
});

registerLocal("ping", [], "show current ping", () => {
  const pingSamples: unknown[] = Array.isArray(player && player.pings)
    ? player.pings
    : [];
  const latest = Number.isFinite(player && player.ping)
    ? Math.max(0, Math.round(player.ping))
    : null;
  const avg = pingSamples.length
    ? Math.round(
        pingSamples.reduce<number>(
          (sum: number, sample: unknown) => sum + (Number(sample) || 0),
          0,
        ) / pingSamples.length,
      )
    : null;

  const resolved = latest ?? avg ?? 0;
  append(`Ping: ${resolved}ms`, "info");
});

// ── Server commands (mirror server/packetHandlers.ts cmds table) ──
//
// Keep arg signatures aligned with the server handlers so the hint
// row matches reality. The server has the final word on permissions.

const onOff = arg("on|off", "boolean", true);
const targetArg = arg("target", "string", true);

registerServer("god", [targetArg, onOff], "toggle god mode (self or target)");
registerServer("noclip", [targetArg, onOff], "toggle noclip (self or target)");
registerServer(
  "smite",
  [targetArg, onOff],
  "toggle smite-on-hit (self or target)",
);
registerServer(
  "forcehide",
  [targetArg, onOff],
  "toggle forcehide (self or target)",
);
registerServer(
  "unlimitedammo",
  [targetArg, onOff],
  "toggle unlimited ammo (self or target)",
);
registerServer("c", [], "show connected players and menu count");
registerServer(
  "count",
  [],
  "alias for /c (show connected players and menu count)",
);
registerServer(
  "ping",
  [arg("name|id", "string", true)],
  "show your ping (devs can target players)",
);
registerServer("bhop", [arg("on|off", "boolean", true)], "toggle bhop");
registerServer(
  "kill",
  [arg("target", "string", true)],
  'kill yourself or a target (examples: /kill, /kill Guest_1, /kill "Wandering Ghost", /kill wanderingghost)',
);
registerServer("end", [], "set match timer to 3 seconds");
registerServer("endless", [], "toggle endless match timer");
registerServer("borders", [onOff], "toggle invisible barriers");
registerServer("forcecol", [onOff], "toggle force collide");
registerServer("server", [], "show current map/mode/player/tick info");
registerServer("cycle", [], "cycle to the next map");
registerServer(
  "map",
  [arg("name", "string", true)],
  "switch map (no arg = show current)",
);
registerServer("maps", [], "list available maps");
registerServer("mode", [arg("alias", "string", true)], "switch game mode");
registerServer("modes", [], "list game modes");
registerServer("team", [arg("red|blue", "string")], "switch teams");
registerServer("teams", [], "list teams and players");
registerServer("players", [], "list connected players (id, name, team)");
registerServer("ais", [], "list active AI entities (sid, name)");
registerServer("kick", [arg("name|id", "string", true)], "kick a player");
registerServer("ban", [arg("name|id", "string", true)], "ban a player account");
registerServer(
  "unban",
  [arg("accountId|name|id", "string", true)],
  "unban an account",
);
registerServer("bans", [], "list banned account ids");
registerServer("ip", [arg("name|id", "string", true)], "show a player's IP");
registerServer(
  "banip",
  [arg("name|id|ip", "string", true)],
  "ban an IP in this lobby",
);
registerServer("unbanip", [arg("ip", "string", true)], "unban an IP");
registerServer("ipbans", [], "list banned IPs");
registerServer(
  "score",
  [arg("amount", "number", true)],
  "add score (default +100)",
);
registerServer(
  "summon",
  [arg("name", "string", true)],
  "spawn a Soul Sanctum AI by name (no arg lists summonable names)",
);
registerServer("game", [arg("text", "string", true)], "send a game command");

// ── State ──────────────────────────────────────────────

const history: string[] = [];
let historyIdx = -1;
let draft = "";

const keyBinds: Record<string, string> = Object.create(null);
const aliases: Record<string, string> = Object.create(null);
const offlineModeOverrides = new WeakMap<GameLike, number>();

function getOfflineModeOverride(game: GameLike | null): number | null {
  if (!game) return null;
  const value = offlineModeOverrides.get(game);
  return Number.isInteger(value) && value >= 0 ? value : null;
}

function setOfflineModeOverride(
  game: GameLike | null,
  modeIndex: number | null,
): void {
  if (!game) return;
  if (!Number.isInteger(modeIndex) || modeIndex < 0) {
    offlineModeOverrides.delete(game);
    return;
  }
  offlineModeOverrides.set(game, modeIndex);
}

// ─── Execution ──────────────────────────────────────────

function getSocketStateLabel(socket: WebSocket | null | undefined): string {
  if (!socket) return "NO_SOCKET";
  switch (socket.readyState) {
    case WebSocket.CONNECTING:
      return "CONNECTING";
    case WebSocket.OPEN:
      return "OPEN";
    case WebSocket.CLOSING:
      return "CLOSING";
    case WebSocket.CLOSED:
      return "CLOSED";
    default:
      return String(socket.readyState);
  }
}

function getCurrentGameId(): string | null {
  try {
    return typeof gameId !== "undefined" ? gameId : null;
  } catch {
    return null;
  }
}

function getCurrentMapLabel(game: GameLike | null): string {
  const mapIndex = game && game.mapIndex;
  const mapName = game?.map?.maps?.[mapIndex]?.name || game?.map?.name || null;
  if (mapName && Number.isInteger(mapIndex)) return `${mapName} (${mapIndex})`;
  if (mapName) return mapName;
  if (Number.isInteger(mapIndex)) return String(mapIndex);
  return "(unknown)";
}

function getCurrentModeLabel(game: GameLike | null): string {
  const modeIndex = game && game.modeIndex;
  const modeName = game?.mode?.alias || game?.mode?.name || null;
  if (modeName && Number.isInteger(modeIndex))
    return `${modeName} (${modeIndex})`;
  if (modeName) return modeName;
  if (Number.isInteger(modeIndex)) return String(modeIndex);
  return "(unknown)";
}

function printServerDetails(): void {
  const socket = io && io.socket;
  const humans = (Game?.players?.list || []).filter(
    (player: Krunker.Player) => player && !player.dummy && !player.spectating,
  );
  const activeHumans = humans.filter(
    (player: Krunker.Player) => player.active === true,
  );
  const status = Game.singlePlayer
    ? "offline"
    : io?.connected
      ? "connected"
      : socket
        ? "disconnected"
        : "no-socket";
  append(
    `Connection: ${status} | socket ${io?.socketId ?? "(none)"} | state ${getSocketStateLabel(socket)}`,
    "info",
  );
  append(`Endpoint: ${socket?.url || location.origin}`, "info");
  append(
    `Lobby: ${getCurrentGameId() || "(none)"} | map ${getCurrentMapLabel(Game)} | mode ${getCurrentModeLabel(Game)}`,
    "info",
  );
  append(
    `Players: ${activeHumans.length}/${humans.length} | host ${location.host}`,
    "info",
  );
}

function parseToggleArg(value: unknown, currentValue: boolean): boolean {
  if (value == null || value === "") return !currentValue;
  const normalised = String(value).toLowerCase();
  if (normalised === "on" || normalised === "1" || normalised === "true") {
    return true;
  }
  if (normalised === "off" || normalised === "0" || normalised === "false") {
    return false;
  }
  return !currentValue;
}

function localCommandUnavailable(message: string): void {
  append(message, "error");
}

function clearPingCounter(): void {
  if (player) {
    player.ping = 0;
    if (Array.isArray(player.pings)) player.pings.length = 0;
  }
  const menuPingText = document.getElementById("menuPingText");
  if (menuPingText) menuPingText.textContent = "0";
  const menuPingIcon = document.getElementById("menuPingIcon");
  if (menuPingIcon) menuPingIcon.style.color = "gray";
}

function offlineBoolCommand(
  property: string,
  label: string,
  args: string[],
  onEnable: ((player: Krunker.Player) => void) | null = null,
): boolean {
  if (!player) return true;
  const next = parseToggleArg(args[0], player[property] === true);
  player[property] = next;
  if (next && typeof onEnable === "function") onEnable(player);
  append(`${label} ${next ? "ON" : "OFF"}`, "info");
  return true;
}

function normaliseOfflineTarget(value: unknown): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function resolveOfflineKillTarget(query: unknown): OfflineKillResolve {
  const needle = normaliseOfflineTarget(query);
  if (!needle) return { kind: null, target: null, reason: "empty" };

  const playerMatches: Array<{ exact: boolean; target: Krunker.Player }> = [];
  const aiMatches: Array<{ exact: boolean; target: AIEntity }> = [];
  for (const candidate of Game.players?.list || []) {
    if (!candidate || candidate.dummy || candidate.active !== true) continue;
    const keys = [candidate.name, candidate.alias, candidate.id]
      .filter(Boolean)
      .map((value) => normaliseOfflineTarget(value));
    if (keys.includes(needle)) {
      playerMatches.push({ exact: true, target: candidate });
      continue;
    }
    if (keys.some((value) => value.startsWith(needle))) {
      playerMatches.push({ exact: false, target: candidate });
    }
  }
  for (const candidate of Game.AI?.ais || []) {
    if (!candidate || candidate.active !== true) continue;
    const keys = [candidate.name, candidate.sid]
      .filter(Boolean)
      .map((value) => normaliseOfflineTarget(value));
    if (keys.includes(needle)) {
      aiMatches.push({ exact: true, target: candidate });
      continue;
    }
    if (keys.some((value) => value.startsWith(needle))) {
      aiMatches.push({ exact: false, target: candidate });
    }
  }

  const exactPlayers = playerMatches.filter((match) => match.exact);
  const exactAis = aiMatches.filter((match) => match.exact);
  if (exactPlayers.length === 1 && exactAis.length === 0) {
    return { kind: "player", target: exactPlayers[0].target, reason: null };
  }
  if (exactAis.length === 1 && exactPlayers.length === 0) {
    return { kind: "ai", target: exactAis[0].target, reason: null };
  }
  if (exactPlayers.length + exactAis.length > 1) {
    return { kind: null, target: null, reason: "ambiguous" };
  }
  if (playerMatches.length === 1 && aiMatches.length === 0) {
    return { kind: "player", target: playerMatches[0].target, reason: null };
  }
  if (aiMatches.length === 1 && playerMatches.length === 0) {
    return { kind: "ai", target: aiMatches[0].target, reason: null };
  }
  if (playerMatches.length + aiMatches.length > 1) {
    return { kind: null, target: null, reason: "ambiguous" };
  }
  return { kind: null, target: null, reason: "not-found" };
}

function killOfflinePlayer(
  sourcePlayer: Krunker.Player | null,
  targetPlayer: Krunker.Player | null,
): boolean {
  if (!Game || !targetPlayer) return false;
  const hadGod = targetPlayer.godMode === true;
  try {
    if (hadGod) targetPlayer.godMode = false;
    if (Game.players && typeof Game.players.kill === "function") {
      Game.players.kill(targetPlayer, sourcePlayer, {});
      return true;
    }
    if (Game.players && typeof Game.players.changeHealth === "function") {
      Game.players.changeHealth(targetPlayer, sourcePlayer, 1e6);
      return true;
    }
    targetPlayer.active = false;
    targetPlayer.health = 0;
    return true;
  } finally {
    if (hadGod) targetPlayer.godMode = true;
  }
}

function killOfflineAI(
  sourcePlayer: Krunker.Player | null,
  targetAI: AIEntity | null,
): boolean {
  if (!targetAI || typeof targetAI.kill !== "function") return false;
  targetAI.kill(sourcePlayer || null);
  if (Game.AI && typeof Game.AI.sync === "function") {
    Game.AI.sync();
  }
  return true;
}

function getOfflineModeEntries(game: GameLike | null): ModeEntry[] {
  const modes = game?.map?.modes;
  if (!Array.isArray(modes)) return [];
  const entries: ModeEntry[] = [];
  for (let i = 0; i < modes.length; i++) {
    const mode = modes[i];
    if (!mode) continue;
    entries.push({
      index: i,
      mode,
      alias: (mode.alias || mode.id || mode.name || `mode${i}`).toLowerCase(),
      name: String(mode.name || mode.id || mode.alias || `Mode ${i}`),
    });
  }
  return entries;
}

function resolveOfflineModeIndex(
  game: GameLike | null,
  query: unknown,
): IndexResolve<{ entries: ModeEntry[] }> {
  const entries = getOfflineModeEntries(game);
  if (!entries.length) return { index: null, entries, reason: "no-modes" };
  const raw = String(query || "").trim();
  if (!raw) return { index: null, entries, reason: "empty" };

  const maybeIndex = Number(raw);
  if (Number.isInteger(maybeIndex)) {
    const hit = entries.find((entry) => entry.index === maybeIndex);
    if (hit) return { index: hit.index, entries, reason: null };
  }

  const needle = raw.toLowerCase();
  const exact = entries.filter((entry) => {
    const id = String(entry.mode?.id || "").toLowerCase();
    const name = String(entry.mode?.name || "").toLowerCase();
    return entry.alias === needle || id === needle || name === needle;
  });
  if (exact.length === 1) {
    return { index: exact[0].index, entries, reason: null };
  }
  if (exact.length > 1) {
    return { index: null, entries, reason: "ambiguous" };
  }

  const partial = entries.filter((entry) => {
    const id = String(entry.mode?.id || "").toLowerCase();
    const name = String(entry.mode?.name || "").toLowerCase();
    return (
      entry.alias.startsWith(needle) ||
      id.startsWith(needle) ||
      name.startsWith(needle)
    );
  });
  if (partial.length === 1) {
    return { index: partial[0].index, entries, reason: null };
  }
  if (partial.length > 1) {
    return { index: null, entries, reason: "ambiguous" };
  }

  return { index: null, entries, reason: "not-found" };
}

function resolveOfflineMapIndex(
  game: GameLike | null,
  query: unknown,
): IndexResolve {
  const maps = game?.map?.maps;
  if (!Array.isArray(maps) || !maps.length) {
    return { index: null, reason: "no-maps" };
  }
  const raw = String(query || "").trim();
  if (!raw) return { index: null, reason: "empty" };

  const maybeIndex = Number(raw);
  if (
    Number.isInteger(maybeIndex) &&
    maybeIndex >= 0 &&
    maybeIndex < maps.length
  ) {
    return { index: maybeIndex, reason: null };
  }

  const needle = raw.toLowerCase();
  const matches: Array<{ exact: boolean; index: number }> = [];
  for (let i = 0; i < maps.length; i++) {
    const map = maps[i];
    if (!map) continue;
    const names = [map.name, map.id, map.key]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());
    if (names.includes(needle)) {
      matches.push({ exact: true, index: i });
      continue;
    }
    if (names.some((value) => value.startsWith(needle))) {
      matches.push({ exact: false, index: i });
    }
  }

  const exact = matches.filter((match) => match.exact);
  if (exact.length === 1) return { index: exact[0].index, reason: null };
  if (exact.length > 1) return { index: null, reason: "ambiguous" };
  if (matches.length === 1) return { index: matches[0].index, reason: null };
  if (matches.length > 1) return { index: null, reason: "ambiguous" };
  return { index: null, reason: "not-found" };
}

function offlineInitAt(mapIndex: number, modeIndex: number): void {
  const io = window.io;
  const initEvent = io && io.events && io.events.init;
  if (
    !Game ||
    (typeof Game.init !== "function" && typeof initEvent !== "function")
  ) {
    throw new Error("game is not ready");
  }
  if (typeof initEvent === "function") {
    initEvent(
      mapIndex,
      modeIndex,
      null,
      null,
      null,
      Game.config || { maps: [mapIndex] },
      null,
      0 as any,
      null,
      null,
      null,
    );
    return;
  }
  Game.init(mapIndex, modeIndex, null, false);
}

const offlineServerCommands: Record<
  string,
  (args: string[], rest?: string) => boolean
> = {
  server() {
    printServerDetails();
    return true;
  },
  cycle() {
    if (!Game) {
      append("Offline cycle unavailable: game is not ready.", "error");
      return true;
    }
    const maps = Game?.map?.maps;
    if (!Array.isArray(maps) || maps.length <= 0) {
      append("Offline cycle unavailable: no map list.", "error");
      return true;
    }

    const currentIndex = Number.isInteger(Game.mapIndex) ? Game.mapIndex : 0;
    const nextIndex = (currentIndex + 1 + maps.length) % maps.length;
    const modeEntries = getOfflineModeEntries(Game);
    const currentMode = Number.isInteger(Game.modeIndex) ? Game.modeIndex : 0;
    const modeOverride = getOfflineModeOverride(Game);
    const modeIndex =
      Number.isInteger(modeOverride) &&
      modeEntries.some((entry) => entry.index === modeOverride)
        ? modeOverride
        : currentMode;

    try {
      offlineInitAt(nextIndex, modeIndex);
      const nextMap = maps[nextIndex];
      const nextName =
        (nextMap && typeof nextMap.name === "string" && nextMap.name) ||
        `Map ${nextIndex}`;
      const modeName =
        Game?.map?.modes?.[modeIndex]?.name ||
        Game?.map?.modes?.[modeIndex]?.id ||
        `mode ${modeIndex}`;
      append(`Cycled to ${nextName} (${nextIndex}) [${modeName}].`, "info");
    } catch (err) {
      append("Cycle failed: " + (err && err.message), "error");
    }
    return true;
  },
  map(args) {
    const maps = Game?.map?.maps;
    if (!Array.isArray(maps) || !maps.length) {
      append("No built-in map list available.", "error");
      return true;
    }

    const raw = args.join(" ").trim();
    if (!raw) {
      append(`Current map: ${getCurrentMapLabel(Game)}`, "info");
      return true;
    }

    const resolved = resolveOfflineMapIndex(Game, raw);
    if (!Number.isInteger(resolved.index)) {
      if (resolved.reason === "ambiguous") {
        append(
          `Map '${raw}' is ambiguous. Use /maps to list choices.`,
          "error",
        );
      } else {
        append(`Map '${raw}' not found. Use /maps to list choices.`, "error");
      }
      return true;
    }

    const mapIndex = resolved.index;
    const modeEntries = getOfflineModeEntries(Game);
    const currentMode = Number.isInteger(Game.modeIndex) ? Game.modeIndex : 0;
    const modeOverride = getOfflineModeOverride(Game);
    const modeIndex =
      Number.isInteger(modeOverride) &&
      modeEntries.some((entry) => entry.index === modeOverride)
        ? modeOverride
        : currentMode;

    try {
      offlineInitAt(mapIndex, modeIndex);
      const nextMap = maps[mapIndex];
      const nextName =
        (nextMap && typeof nextMap.name === "string" && nextMap.name) ||
        (nextMap && typeof nextMap.id === "string" && nextMap.id) ||
        `Map ${mapIndex}`;
      append(`Switched map to ${nextName} (${mapIndex}).`, "info");
    } catch (err) {
      append("Map switch failed: " + (err && err.message), "error");
    }
    return true;
  },
  mode(args) {
    if (!Game) {
      append("Offline mode unavailable: game is not ready.", "error");
      return true;
    }
    const raw = args.join(" ").trim();
    if (!raw) {
      append(`Current mode: ${getCurrentModeLabel(Game)}`, "info");
      const modeOverride = getOfflineModeOverride(Game);
      if (Number.isInteger(modeOverride)) {
        append(`Cycle mode override: ${modeOverride}`, "hint");
      }
      return true;
    }

    if (raw.toLowerCase() === "default" || raw.toLowerCase() === "reset") {
      setOfflineModeOverride(Game, null);
      append("Cycle mode override cleared.", "info");
      return true;
    }

    const resolved = resolveOfflineModeIndex(Game, raw);
    if (!Number.isInteger(resolved.index)) {
      if (resolved.reason === "ambiguous") {
        append(
          `Mode '${raw}' is ambiguous. Use /modes to list choices.`,
          "error",
        );
      } else if (resolved.reason === "no-modes") {
        append("No mode list available for this map.", "error");
      } else {
        append(`Mode '${raw}' not found. Use /modes to list choices.`, "error");
      }
      return true;
    }

    const modeIndex = resolved.index;
    const mapIndex = Number.isInteger(Game.mapIndex) ? Game.mapIndex : 0;
    try {
      setOfflineModeOverride(Game, modeIndex);
      offlineInitAt(mapIndex, modeIndex);
      const modeName =
        Game?.map?.modes?.[modeIndex]?.name ||
        Game?.map?.modes?.[modeIndex]?.id ||
        `mode ${modeIndex}`;
      append(`Mode set to ${modeName} (${modeIndex}).`, "info");
    } catch (err) {
      append("Mode switch failed: " + (err && err.message), "error");
    }
    return true;
  },
  modes() {
    if (!Game) {
      append("No active game.", "error");
      return true;
    }
    const entries = getOfflineModeEntries(Game);
    if (!entries.length) {
      append("No modes available.", "info");
      return true;
    }
    append(`Modes (${entries.length}):`, "info");
    for (const entry of entries) {
      const id = entry.mode?.id ? ` id:${entry.mode.id}` : "";
      const marker = entry.index === Game.modeIndex ? " *" : "";
      append(`  ${entry.index}: ${entry.name}${id}${marker}`, "hint");
    }
    const modeOverride = getOfflineModeOverride(Game);
    if (Number.isInteger(modeOverride)) {
      append(`Cycle mode override: ${modeOverride}`, "info");
    }
    return true;
  },
  maps() {
    const maps = Game?.map?.maps;
    if (!Array.isArray(maps) || !maps.length) {
      append("No built-in map list available.", "error");
      return true;
    }
    append(`Built-in maps (${maps.length}):`, "info");
    append(`${maps.map((e) => e.name).join(", ")}`);
    return true;
  },
  borders(args) {
    if (!Game) {
      append("Offline borders unavailable: game is not ready.", "error");
      return true;
    }
    const currentEnabled = Game.config?.disableB !== true;
    const nextEnabled = parseToggleArg(args[0], currentEnabled);
    if (nextEnabled === currentEnabled) {
      append(`Borders already ${currentEnabled ? "ON" : "OFF"}`, "info");
      return true;
    }
    applyBordersFromServer(nextEnabled);
    append(`Borders ${nextEnabled ? "ON" : "OFF"}`, "info");
    return true;
  },
  god(args) {
    return offlineBoolCommand("godMode", "God mode", args, (player) => {
      if (Number.isFinite(player.maxHealth)) player.health = player.maxHealth;
    });
  },
  noclip(args) {
    return offlineBoolCommand("noclip", "Noclip", args);
  },
  smite(args) {
    return offlineBoolCommand("smite", "Smite", args);
  },
  forcehide(args) {
    return offlineBoolCommand("forceHide", "Forcehide", args);
  },
  unlimitedammo(args) {
    return offlineBoolCommand(
      "unlimitedAmmo",
      "Unlimited ammo",
      args,
      (player) => {
        player.canThrow = true;
      },
    );
  },
  kill(args) {
    if (!player) return true;
    const query = args.join(" ").trim();
    if (!query) {
      killOfflinePlayer(player, player);
      append("You killed yourself.", "info");
      return true;
    }
    const resolved = resolveOfflineKillTarget(query);
    if (!resolved.target) {
      append(
        resolved.reason === "ambiguous"
          ? `Kill target '${query}' is ambiguous.`
          : `Kill target '${query}' not found.`,
        "error",
      );
      return true;
    }
    if (resolved.kind === "player") {
      killOfflinePlayer(player, resolved.target);
      append(
        `Killed player ${resolved.target.name || resolved.target.alias || resolved.target.id}.`,
        "info",
      );
      return true;
    }
    if (resolved.kind === "ai") {
      killOfflineAI(player, resolved.target);
      append(
        `Killed AI ${resolved.target.name || resolved.target.sid}.`,
        "info",
      );
      return true;
    }
    return true;
  },
  score(args) {
    if (!player || !Game?.players || typeof Game.players.score !== "function") {
      return true;
    }
    const amount = args.length ? Number(args[0]) : 100;
    if (!Number.isFinite(amount) || amount === 0) {
      append("Usage: score <amount> (default 100).", "error");
      return true;
    }
    Game.players.score(player, amount);
    append(
      `${amount > 0 ? "+" : ""}${amount} score (total ${player.score}).`,
      "info",
    );
    return true;
  },
  team(args) {
    const mode = Game?.mode;
    if (!player || !Game) return true;
    if (
      !mode ||
      !mode.teams ||
      mode.friendly ||
      mode.clanWar ||
      mode.convTeam
    ) {
      append("/team only works in team modes.", "error");
      return true;
    }
    const arg = (args[0] || "").toLowerCase();
    const next =
      arg === "red" || arg === "1"
        ? 1
        : arg === "blue" || arg === "2"
          ? 2
          : null;
    if (next == null) {
      append("Usage: /team red|blue", "error");
      return true;
    }
    if (player.team === next) {
      append(`Already on ${arg}.`, "info");
      return true;
    }
    player.team = next;
    killOfflinePlayer(player, player);
    append(`Switched to ${arg} team.`, "info");
    return true;
  },
  teams() {
    const players = Game?.players?.list;
    const mode = Game?.mode;
    if (!players || !players.length) {
      append("No players.", "info");
      return true;
    }
    if (!mode || !mode.teams || mode.convTeam) {
      append("Not a team mode.", "info");
      return true;
    }
    const groups = new Map();
    for (const p of players) {
      if (!p || p.spectating) continue;
      const team = p.team || 0;
      if (!groups.has(team)) groups.set(team, []);
      groups.get(team).push(p.name || `Guest_${p.sid}`);
    }
    for (const team of [...groups.keys()].sort((left, right) => left - right)) {
      const label = team === 1 ? "Red" : team === 2 ? "Blue" : `Team ${team}`;
      const names = groups.get(team);
      append(`${label} (${names.length}): ${names.join(", ")}`, "info");
    }
    return true;
  },
  end() {
    if (!Game) return true;
    Game.endTimer = 0;
    Game.gameTimer = 3e3;
    Game.lastTimer = null;
    append("Match timer set to 3 seconds.", "info");
    return true;
  },
  endless() {
    if (!Game) return true;
    const isEndless = Game.endTimer === "inf";
    if (isEndless) {
      const restored =
        Game.forceStaticMap === true
          ? 240000
          : Game.mode && Game.mode.gameTime == null
            ? (Game.config && Game.config.gameTime ? Game.config.gameTime : 4) *
              60000
            : Game.mode.gameTime;
      Game.endTimer = 0;
      Game.gameTimer = restored;
      Game.lastTimer = null;
      append("Match timer resumed.", "info");
      return true;
    }
    Game.endTimer = "inf";
    Game.lastTimer = null;
    append("Match timer paused.", "info");
    return true;
  },
};

function maybeRunOfflineServerCommand(line: string): boolean {
  if (!Game.singlePlayer) return false;
  const stripped = line.startsWith("/") ? line.slice(1) : line;
  const space = stripped.indexOf(" ");
  const name = (
    space === -1 ? stripped : stripped.slice(0, space)
  ).toLowerCase();
  const rest = space === -1 ? "" : stripped.slice(space + 1);
  const handler = offlineServerCommands[name];
  if (!handler) return false;
  try {
    handler(parseArgs(rest), rest);
  } catch (err) {
    append("error: " + (err && err.message), "error");
  }
  return true;
}

function sendServer(line: string): void {
  if (maybeRunOfflineServerCommand(line)) {
    return;
  }
  if (!window.io || typeof window.io.send !== "function") {
    append("not connected to server", "error");
    return;
  }
  try {
    window.io.send("ct", line);
  } catch (err) {
    append("send failed: " + (err && err.message), "error");
  }
}

function parseArgs(rest: string): string[] {
  // Source-style: split on whitespace, respect double-quoted strings.
  const out: string[] = [];
  const re = /"([^"]*)"|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(rest)) !== null) {
    out.push(m[1] !== undefined ? m[1] : m[2]);
  }
  return out;
}

export function exec(line: string): void {
  line = line.trim();
  if (!line) return;
  append(`] ${line}`, "cmd"); // Allow leading `/` so users can paste literal chat-style commands.
  const stripped = line.startsWith("/") ? line.slice(1) : line;
  const space = stripped.indexOf(" ");
  const name = (
    space === -1 ? stripped : stripped.slice(0, space)
  ).toLowerCase();
  const rest = space === -1 ? "" : stripped.slice(space + 1);
  const args = parseArgs(rest);

  const c = cmds[name];
  if (!c) {
    // append(`unknown command: ${name}`, "error");
    return;
  }

  if (c.kind === "local") {
    try {
      c.run(args, rest);
    } catch (err) {
      append("error: " + (err && err.message), "error");
    }
  } else {
    sendServer("/" + stripped);
  }
}

// ─── Autocomplete / hints ───────────────────────────────

function fmtArgs(args: CommandArg[]): string {
  return args
    .map((a: CommandArg) =>
      a.optional ? `${a.name}: ${a.type}?` : `${a.name}: ${a.type}`,
    )
    .join("  ");
}

function clearHints(): void {
  hints.innerHTML = "";
  hints.classList.remove("isOpen");
}

function addHintItem(
  label: string,
  meta: string | null,
  active: boolean,
  onPick: (() => void) | null = null,
): void {
  const item = document.createElement("div");
  item.className = "hintItem" + (active ? " isActive" : "");
  item.textContent = label;
  if (meta) {
    const span = document.createElement("span");
    span.className = "hintMeta";
    span.textContent = `  ${meta}`;
    item.append(span);
  }
  if (onPick) {
    item.addEventListener("mousedown", (e) => {
      e.preventDefault();
      onPick();
      input.focus();
    });
  }
  hints.append(item);
}

function updateHints(): void {
  const v = input.value;
  clearHints();
  if (!v) return;
  const space = v.indexOf(" ");
  const head = (space === -1 ? v : v.slice(0, space)).toLowerCase();
  const headStripped = head.startsWith("/") ? head.slice(1) : head;

  if (space === -1) {
    const matches = Object.keys(cmds)
      .filter((n) => n.startsWith(headStripped))
      .slice(0, 12);
    if (!matches.length) return;
    hints.classList.add("isOpen");
    const activeIndex = Math.max(0, matches.indexOf(headStripped));
    for (let i = 0; i < matches.length; i++) {
      const name = matches[i];
      addHintItem(name, null, i === activeIndex, () => {
        input.value = name + " ";
        updateHints();
      });
    }
    return;
  }

  const c = cmds[headStripped];
  if (c) {
    hints.classList.add("isOpen");
    addHintItem(headStripped, `${fmtArgs(c.args)}  —  ${c.desc}`, true);
  } else {
    hints.classList.add("isOpen");
    addHintItem("(unknown command)", "will not be sent", true);
  }
}

function tabComplete(): void {
  const v = input.value;
  const space = v.indexOf(" ");
  if (space !== -1) return; // only complete the command name
  const headStripped = v.startsWith("/") ? v.slice(1) : v;
  const matches = Object.keys(cmds).filter((n) => n.startsWith(headStripped));
  if (matches.length === 1) {
    input.value = matches[0] + " ";
  } else if (matches.length > 1) {
    let prefix = matches[0];
    for (const m of matches) {
      while (!m.startsWith(prefix)) prefix = prefix.slice(0, -1);
    }
    if (prefix.length > headStripped.length) input.value = prefix;
  }
  updateHints();
}

// ─── Input handling ─────────────────────────────────────

// History/Tab/Escape navigation must use keydown — those keys never
// fire `input` events because they don't insert characters. Listen on
// window capture so the game cannot swallow them at document capture
// before the focused console input ever sees the event.
function handleConsoleKeydown(e: KeyboardEvent): void {
  if (!visible) return;

  const activeEl = document.activeElement;
  const focusInConsole = activeEl === input || activeEl === logs;

  if (e.key === "Escape" && focusInConsole) {
    e.preventDefault();
    e.stopPropagation();
    toggleConsole();
    return;
  }

  if (activeEl !== input) return;

  if (e.key === "ArrowUp") {
    e.preventDefault();
    e.stopPropagation();
    if (!history.length) return;
    if (historyIdx === -1) {
      draft = input.value;
      historyIdx = history.length - 1;
    } else if (historyIdx > 0) {
      historyIdx--;
    }
    input.value = history[historyIdx];
    updateHints();
  } else if (e.key === "ArrowDown") {
    e.preventDefault();
    e.stopPropagation();
    if (historyIdx === -1) return;
    if (historyIdx < history.length - 1) {
      historyIdx++;
      input.value = history[historyIdx];
    } else {
      historyIdx = -1;
      input.value = draft;
    }
    updateHints();
  } else if (e.key === "Tab") {
    e.preventDefault();
    e.stopPropagation();
    tabComplete();
  }
}

window.addEventListener("keydown", handleConsoleKeydown, true);

// `input` event fires after the character is actually inserted, which
// means it survives any upstream capture-phase keydown listener that
// calls stopImmediatePropagation (the engine does this for several
// keys). We use it both to refresh hints and to catch the toggle key
// (backtick / configured bind) — strip the inserted char and toggle.
input.addEventListener("input", (e) => {
  const bindKey = (window.devConsoleKey || "Backquote").replace(/^Key/, "");
  const toggleChars = new Set(["`", "~"]);
  // Map common bind codes to their printable character so a custom bind
  // can also toggle via this path.
  const bindChar =
    { Backquote: "`", Minus: "-", Equal: "=" }[
      window.devConsoleKey || "Backquote"
    ] || (bindKey.length === 1 ? bindKey.toLowerCase() : null);
  if (bindChar) toggleChars.add(bindChar);

  const data = (e as InputEvent).data;
  if (data && toggleChars.has(data)) {
    // Remove the just-inserted toggle character from the input value.
    const pos = input.selectionStart ?? input.value.length;
    const before = input.value.slice(0, Math.max(0, pos - data.length));
    const after = input.value.slice(pos);
    input.value = before + after;
    try {
      input.setSelectionRange(before.length, before.length);
    } catch {}
    toggleConsole();
    return;
  }

  updateHints();
});

// Form submit handles Enter — survives upstream stopImmediatePropagation
// on keydown. Also covers any browser autocomplete confirmation paths.
inputRow.addEventListener("submit", (e) => {
  e.preventDefault();
  e.stopPropagation();
  const line = input.value;
  input.value = "";
  clearHints();
  if (line.trim()) {
    history.push(line);
    if (history.length > 200) history.shift();
  }
  historyIdx = -1;
  draft = "";
  exec(line);
});

// Also stop key events from bubbling to the document while the
// console has focus (so WASD doesn't move the player when typing).
for (const t of ["keydown", "keyup", "keypress"]) {
  overlay.addEventListener(t, (e) => e.stopPropagation(), true);
}

// Click anywhere on the overlay (outside the modal) to close.
// (Disabled — the overlay now has pointer-events: none so the game
// stays interactive; clicks only hit the modal/grip themselves.)

// ─── Drag the modal by its titlebar ────────────────────────

// Persist position/size across sessions.
const GEOM_KEY = "krk_dev_console_geom";
function loadGeom(): ConsoleGeom | null {
  try {
    const raw = localStorage.getItem(GEOM_KEY);
    if (!raw) return null;
    const g = JSON.parse(raw);
    if (!g || typeof g !== "object") return null;
    return g as ConsoleGeom;
  } catch {
    return null;
  }
}
let saveGeomTimer: ReturnType<typeof setTimeout> | 0 = 0;
function saveGeom(): void {
  clearTimeout(saveGeomTimer);
  saveGeomTimer = setTimeout(() => {
    if (modal.dataset.posCommitted !== "1") return;
    try {
      localStorage.setItem(
        GEOM_KEY,
        JSON.stringify({
          left: parseInt(modal.style.left || "0", 10),
          top: parseInt(modal.style.top || "0", 10),
          width: modal.offsetWidth,
          height: modal.offsetHeight,
        }),
      );
    } catch {}
  }, 150);
}

function clampPos(): void {
  const w = modal.offsetWidth;
  const h = modal.offsetHeight;
  const left = parseInt(modal.style.left || "0", 10);
  const top = parseInt(modal.style.top || "0", 10);
  const maxX = Math.max(0, window.innerWidth - w);
  const maxY = Math.max(0, window.innerHeight - h);
  modal.style.left = Math.max(0, Math.min(maxX, left)) + "px";
  modal.style.top = Math.max(0, Math.min(maxY, top)) + "px";
}

function commitInitialPos(): void {
  // Switch from CSS-centered (left: 50%; transform) to absolute pixels
  // anchored at the current visual position. Idempotent.
  if (modal.dataset.posCommitted === "1") return;
  const rect = modal.getBoundingClientRect();
  modal.style.transform = "none";
  modal.style.left = rect.left + "px";
  modal.style.top = rect.top + "px";
  modal.dataset.posCommitted = "1";
}

// Restore saved geometry on load (deferred so the modal has laid out).
function restoreGeom(): void {
  const g = loadGeom();
  if (!g) return;
  if (Number.isFinite(g.width) && g.width >= 280) {
    modal.style.width = g.width + "px";
  }
  if (Number.isFinite(g.height) && g.height >= 160) {
    modal.style.height = g.height + "px";
  }
  if (Number.isFinite(g.left) && Number.isFinite(g.top)) {
    modal.style.transform = "none";
    modal.style.left = g.left + "px";
    modal.style.top = g.top + "px";
    modal.dataset.posCommitted = "1";
    clampPos();
  }
}
if (document.body) restoreGeom();
else document.addEventListener("DOMContentLoaded", restoreGeom, { once: true });

{
  let dragging = false;
  let dx = 0;
  let dy = 0;
  header.addEventListener("mousedown", (e) => {
    if (e.button !== 0 || e.target === closeBtn) return;
    commitInitialPos();
    dragging = true;
    const rect = modal.getBoundingClientRect();
    dx = e.clientX - rect.left;
    dy = e.clientY - rect.top;
    e.preventDefault();
  });
  window.addEventListener("mousemove", (e) => {
    if (!dragging) return;
    modal.style.left = e.clientX - dx + "px";
    modal.style.top = e.clientY - dy + "px";
    clampPos();
  });
  window.addEventListener("mouseup", () => {
    if (dragging) saveGeom();
    dragging = false;
  });
}

// ─── Resize via bottom-right grip ─────────────────────────
{
  let resizing = false;
  let startX = 0;
  let startY = 0;
  let startW = 0;
  let startH = 0;
  grip.addEventListener("mousedown", (e) => {
    if (e.button !== 0) return;
    commitInitialPos();
    resizing = true;
    startX = e.clientX;
    startY = e.clientY;
    startW = modal.offsetWidth;
    startH = modal.offsetHeight;
    e.preventDefault();
  });
  window.addEventListener("mousemove", (e) => {
    if (!resizing) return;
    const w = Math.max(280, startW + (e.clientX - startX));
    const h = Math.max(160, startH + (e.clientY - startY));
    modal.style.width = w + "px";
    modal.style.height = h + "px";
  });
  window.addEventListener("mouseup", () => {
    if (resizing) saveGeom();
    resizing = false;
  });
}

// ─── Visibility ─────────────────────────────────────────

export let visible = false;

export function update(goToGame = true): void {
  overlay.style.display = visible ? "block" : "none";
  if (visible) {
    // Defer focus so the toggling `~` keydown doesn't land in the input.
    setTimeout(() => input.focus(), 0);
    try {
      document.exitPointerLock && document.exitPointerLock();
    } catch {}
  } else if (goToGame) {
    input.blur();
    CONTROLS.locklessChange(true);
    (
      RENDER.renderer as { domElement?: HTMLElement } | undefined
    )?.domElement?.requestPointerLock?.();
  }
}

export function toggleConsole(goToGame = true): void {
  visible = !visible;
  update(goToGame);
}

// ─── Server reply mirroring ─────────────────────────────
//
// Tap incoming `ch` packets so command replies (`Server: God mode ON`)
// surface in the console. The compiled engine installs handlers on
// `io.events` once the connection opens; objects.js exposes the `io`
// instance to window. Poll until the table appears, then wrap.

function numberTuple(value: unknown, length: number): number[] | null {
  if (!Array.isArray(value) || value.length < length) return null;
  const tuple = value.slice(0, length).map((part) => Number(part));
  return tuple.every(Number.isFinite) ? tuple : null;
}

function forceCollideKind(source: ForceCollideSourceObject): string {
  const rawKind = source.i ?? source.id ?? "CUBE";
  const kind = String(rawKind).toUpperCase();
  return kind === "0" ? "CUBE" : kind;
}

function sourceHasNoCollision(source: ForceCollideSourceObject): boolean {
  return !!(source.l || source.col);
}

function buildInjectedForceCollideObject(
  source: ForceCollideSourceObject,
  sourceIndex: number,
): ForceCollideInjectedObject | null {
  const position = numberTuple(source.p, 3);
  const size = numberTuple(source.s, 3);
  if (!position || !size) return null;

  const kind = forceCollideKind(source);
  const halfWidth = size[0] / 2;
  const halfHeight = size[1] / 2;
  const halfLength = size[2] / 2;
  const uid = -900000 - sourceIndex;
  const injected: ForceCollideInjectedObject = {
    __forceColInjected: true,
    __forceColSourceIndex: sourceIndex,
    active: true,
    collidable: true,
    noCol: false,
    noVis: true,
    sid: uid,
    uid,
    x: position[0],
    y: position[1] + halfHeight,
    z: position[2],
    width: halfWidth,
    height: halfHeight,
    length: halfLength,
    ter: true,
    type: 0,
    wallJumpable: source.wj == null,
  };

  if (source.bo != null) injected.isBorder = source.bo;
  if (source.pe != null) injected.penetrable = source.pe;

  if (kind === "CYLINDER") {
    const radius = Math.min(size[0], size[2]) / 2;
    injected.collT = "cylinder";
    injected.width = radius;
    injected.length = radius;
    return injected;
  }

  if (kind === "PLANE" || kind === "BILLBOARD" || kind === "RUNE") {
    injected.y = position[1] + (kind === "RUNE" ? 0.01 : 0);
    injected.height = 0;
    injected.width = halfWidth;
    injected.length = halfLength;
    if (kind === "BILLBOARD") injected.type = undefined;
    return injected;
  }

  if (kind === "CUBE" || kind === "CUSTOM_ASSET") {
    if (kind === "CUSTOM_ASSET") injected.complexMesh = true;
    return injected;
  }

  if (
    kind === "POINT_LIGHT" ||
    kind === "SOUND_EMITTER" ||
    kind === "SPECTATE_CAM" ||
    kind === "SIGN" ||
    kind === "LIQUID"
  ) {
    return null;
  }

  return injected;
}

function rebuildForceCollideGrid(manager: Krunker.AnyRecord): void {
  manager.grids = {};
  if (typeof manager.genQuads === "function") manager.genQuads();
}

function applyBordersFromServer(enabled: boolean): void {
  if (!Game) return;
  Game.config ||= {};
  Game.config.disableB = !enabled;
}

function originalForceCollideObjects(): ForceCollideSourceObject[] {
  const stored = window.__krunkerOriginalMapData?.current;
  const mapData =
    stored ||
    (Game &&
    Game.map &&
    Array.isArray(Game.map.maps) &&
    Game.map.maps[Game.mapIndex]
      ? Game.map.maps[Game.mapIndex]
      : null);
  return mapData && Array.isArray(mapData.objects)
    ? (mapData.objects as ForceCollideSourceObject[])
    : [];
}

function injectForceCollideObjects(
  manager: Krunker.AnyRecord,
): ForceCollideInjectedObject[] {
  const runtimeObjects = Array.isArray(manager.objects)
    ? (manager.objects as ForceCollideObject[])
    : [];
  const existingSourceIndexes = new Set(
    runtimeObjects
      .filter((obj) => obj && obj.__forceColInjected)
      .map((obj) => obj.__forceColSourceIndex),
  );
  const injected: ForceCollideInjectedObject[] = [];
  const sourceObjects = originalForceCollideObjects();

  for (let sourceIndex = 0; sourceIndex < sourceObjects.length; sourceIndex++) {
    const source = sourceObjects[sourceIndex];
    if (!source || typeof source !== "object") continue;
    if (!sourceHasNoCollision(source)) continue;
    if (existingSourceIndexes.has(sourceIndex)) continue;
    const obj = buildInjectedForceCollideObject(source, sourceIndex);
    if (!obj) continue;
    runtimeObjects.push(obj);
    injected.push(obj);
  }

  if (injected.length) rebuildForceCollideGrid(manager);
  return injected;
}

function removeInjectedForceCollideObjects(
  manager: Krunker.AnyRecord,
  injected: ForceCollideInjectedObject[],
): number {
  if (!Array.isArray(manager.objects) || !injected.length) return 0;
  const injectedSet = new Set(injected);
  let removed = 0;
  for (let index = manager.objects.length - 1; index >= 0; index--) {
    if (injectedSet.has(manager.objects[index])) {
      manager.objects.splice(index, 1);
      removed++;
    }
  }
  if (removed) rebuildForceCollideGrid(manager);
  return removed;
}

function applyForceCollideFromServer(enabled: boolean): void {
  const manager = Game && Game.map && Game.map.manager;
  const runtimeObjects =
    manager && Array.isArray(manager.objects) ? manager.objects : [];
  const mapObjects =
    Game &&
    Game.map &&
    Array.isArray(Game.map.maps) &&
    Game.map.maps[Game.mapIndex] &&
    Array.isArray(Game.map.maps[Game.mapIndex].objects)
      ? Game.map.maps[Game.mapIndex].objects
      : [];
  if (runtimeObjects.length === 0 && mapObjects.length === 0) return;

  const state = (manager.__forceColClientState ||
    null) as ForceCollideState | null;
  if (enabled) {
    if (state && state.enabled) return;
    const entries: ForceCollideEntry[] = [];
    for (const obj of [...runtimeObjects, ...mapObjects]) {
      if (!obj || typeof obj !== "object") continue;
      entries.push({
        obj,
        hasNoCol: Object.prototype.hasOwnProperty.call(obj, "noCol"),
        noCol: obj.noCol,
        hasCollidable: Object.prototype.hasOwnProperty.call(obj, "collidable"),
        collidable: obj.collidable,
        hasMeshCollidable:
          !!obj.meshRef &&
          Object.prototype.hasOwnProperty.call(obj.meshRef, "collidable"),
        meshCollidable: obj.meshRef ? obj.meshRef.collidable : undefined,
      });
      if (obj.noCol) obj.noCol = false;
      if (obj.collidable !== true) {
        obj.collidable = true;
      }
      if (obj.meshRef && obj.meshRef.collidable !== true) {
        obj.meshRef.collidable = true;
      }
    }
    const injected = injectForceCollideObjects(manager);
    manager.__forceColClientState = { enabled: true, entries, injected };
    return;
  }

  if (!state || !state.enabled) return;
  for (const entry of state.entries || []) {
    const obj = entry && entry.obj;
    if (!obj) continue;
    if (entry.hasNoCol) obj.noCol = entry.noCol;
    else if (Object.prototype.hasOwnProperty.call(obj, "noCol"))
      delete obj.noCol;

    if (entry.hasCollidable) obj.collidable = entry.collidable;
    else if (Object.prototype.hasOwnProperty.call(obj, "collidable")) {
      delete obj.collidable;
    }

    if (obj.meshRef) {
      if (entry.hasMeshCollidable)
        obj.meshRef.collidable = entry.meshCollidable;
      else if (
        Object.prototype.hasOwnProperty.call(obj.meshRef, "collidable")
      ) {
        delete obj.meshRef.collidable;
      }
    }
  }
  removeInjectedForceCollideObjects(manager, state.injected || []);
  manager.__forceColClientState = null;
}

function hookChat(): void {
  let installed = false;
  const wrap = (
    events: Krunker.IOEvents,
    name: string,
    fmt: (...args: any[]) => string | null | undefined,
  ): void => {
    const orig = events[name] as WrappedHandler | undefined;
    if (typeof orig !== "function" || orig.__devConsoleWrapped) return;
    const wrapped = function (this: unknown, ...args: any[]): unknown {
      try {
        const msg = fmt(...args);
        if (msg) append(msg, "reply");
      } catch {}
      return orig.apply(this, arguments);
    };
    wrapped.__devConsoleWrapped = true;
    events[name] = wrapped;
  };
  const onOff = (v: unknown): string => (v ? "Enabled" : "Disabled");
  const tryInstall = (): void => {
    if (installed) return;
    const io = window.io;
    const events = io && io.events;
    if (!events || typeof events.ch !== "function") return;
    wrap(events, "ch", (name: string | null, text: unknown /*, type */) => {
      const cleaned = String(text == null ? "" : text).replace(/<[^>]+>/g, "");
      return `${name || "Server"}: ${cleaned}`;
    });
    wrap(events, "noc", (v) => `Noclip - ${onOff(v)}`);
    wrap(events, "unl", (v) => `Unlimited Ammo - ${onOff(v)}`);
    wrap(events, "fh", (v) => `Force Hide - ${onOff(v)}`);
    const eventTable = events as Krunker.IOEvents & {
      borders?: WrappedHandler;
    };
    if (!eventTable.borders || !eventTable.borders.__devConsoleWrapped) {
      const origBorders =
        typeof eventTable.borders === "function" ? eventTable.borders : null;
      const wrappedBorders = function (this: unknown, v: unknown): unknown {
        try {
          applyBordersFromServer(!!v);
        } catch {}
        if (origBorders) return origBorders.apply(this, arguments);
      };
      wrappedBorders.__devConsoleWrapped = true;
      eventTable.borders = wrappedBorders;
    }
    if (!events.forcecol || !events.forcecol.__devConsoleWrapped) {
      const origForcecol =
        typeof events.forcecol === "function" ? events.forcecol : null;
      const wrappedForcecol = function (this: unknown, v: unknown): unknown {
        try {
          applyForceCollideFromServer(!!v);
        } catch {}
        if (origForcecol) return origForcecol.apply(this, arguments);
      };
      wrappedForcecol.__devConsoleWrapped = true;
      events.forcecol = wrappedForcecol;
    }
    installed = true;
  };
  const t = setInterval(() => {
    tryInstall();
    if (installed) clearInterval(t);
  }, 500);
}

hookChat();

function hookOfflineChatCommands(): void {
  const BHOP_MODE_NAMES: Record<number, string> = {
    0: "Disabled",
    1: "Hold Jump/Crouch",
  };

  function resolveBhopMode(arg: unknown, currentMode: number): number | null {
    if (!arg) return currentMode > 0 ? 0 : 1;
    const raw = String(arg).trim().toLowerCase();
    if (["off", "0", "disable", "disabled"].includes(raw)) return 0;
    if (
      [
        "on",
        "1",
        "enable",
        "enabled",
        "hold",
        "manual",
        "held",
        "manualslide",
        "holdslide",
        "jumpcrouch",
        "both",
        "2",
        "3",
        "4",
      ].includes(raw)
    )
      return 1;
    return null;
  }

  function maybeHandleClientBhopCommand(
    label: string,
    payload: unknown,
  ): boolean {
    if (label !== "ct" && label !== "ch") return false;
    if (typeof payload !== "string") return false;
    const line = payload.trim();
    if (!line.toLowerCase().startsWith("/bhop")) return false;

    const rest = line.slice(5).trim();
    const currentMode = window.bhopMode | 0;
    const nextMode = resolveBhopMode(rest, currentMode);
    if (nextMode == null) {
      append("Usage: /bhop [on|off|held]", "error");
      return true;
    }

    window.bhopMode = nextMode;
    window.bhopEnabled = nextMode > 0;
    try {
      localStorage.setItem("bhop", String(nextMode));
    } catch {}
    const msg = `Bhop - ${BHOP_MODE_NAMES[nextMode] || "Hold Jump/Crouch"}`;
    append(msg, "reply");
    try {
      if (typeof window.notify === "function") window.notify(msg);
    } catch {}
    return true;
  }

  let installed = false;
  const tryInstall = (): void => {
    if (installed) return;
    const io = window.io;
    if (!io || typeof io.send !== "function") return;
    if ((io.send as any).__devConsoleOfflineWrapped) {
      installed = true;
      return;
    }
    const origSend = io.send;
    io.send = function (
      this: Krunker.IO,
      label: string,
      ...data: any[]
    ): unknown {
      if (maybeHandleClientBhopCommand(label, data[0])) {
        return;
      }
      if (label === "ct" && this && this.socket === null) {
        const line = data[0];
        if (typeof line === "string" && maybeRunOfflineServerCommand(line)) {
          return;
        }
      }
      return origSend.apply(this, arguments as any);
    };
    (io.send as any).__devConsoleOfflineWrapped = true;
    installed = true;
  };
  const t = setInterval(() => {
    tryInstall();
    if (installed) clearInterval(t);
  }, 500);
}

hookOfflineChatCommands();

// ─── Key bind dispatch ──────────────────────────────────
//
// Source-style `bind` runs the bound command on keydown. We only
// fire when the console is closed and focus isn't in another input.

document.addEventListener("keydown", (e) => {
  if (e.repeat) return;
  if (visible) return;
  const tag = e.target instanceof Element ? e.target.tagName : "";
  if (tag === "INPUT" || tag === "TEXTAREA") return;
  const cmd = keyBinds[e.key] || keyBinds[e.code];
  if (cmd) {
    e.preventDefault();
    exec(cmd);
  }
});

// ─── Bootstrap banner ───────────────────────────────────

append("Krunker developer console - type `help` for commands.", "info");
append("Press ~ to toggle, Esc to close, Tab to autocomplete.", "hint");

// Expose for devtools.
window.devConsole = {
  exec,
  print,
  toggleConsole,
  update,
  get visible() {
    return visible;
  },
  set visible(value) {
    visible = !!value;
  },
};
